import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, ViewChild, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { AbilityService } from 'ability';
import { I18NextPipe } from 'angular-i18next';
import {
  AlertComponent,
  ButtonComponent,
  MbsLabelSize,
  MbsPopupType,
  MbsSize,
  ModalComponent,
  ModalFooterDirective,
  ModalService,
  RadioComponent,
  SidepanelService,
  StatusComponent,
  ToastService
} from 'mbs-ui-kit';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { BuildsFacade } from 'mbs-ui-shared@facades/builds.facade';
import { ComputersFacade } from 'mbs-ui-shared@facades/computers.facade';
import { BuildType } from 'mbs-ui-shared@models/BuildType.enum';
import Computer, { AgentType, ComputersFiltersType, GetComputersParams } from 'mbs-ui-shared@models/Computer';
import {
  AgentAvailableBuildVersions,
  AgentCommand,
  ComputerAvailableVersions,
  ComputerBasedModal
} from 'mbs-ui-shared@models/ComputersModals';
import { getDownloadBuildType } from 'mbs-ui-shared@models/InstallAgent';
import { PermissionsEnum } from 'mbs-ui-shared@models/PermissionsEnum';
import { Build, BuildVersionType, isRevokedCertificateVersion } from 'mbs-ui-shared@models/build';
import { GroupActionMode, ParamsForceUpdate, UpdateBuild } from 'mbs-ui-shared@models/rm/remote-command-model';
import { CommonResponseType } from 'mbs-ui-shared@services/build-service/build-service.types';
import { BuildService } from 'mbs-ui-shared@services/build-service/build.service';
import { RmCommandsWrapper } from 'mbs-ui-shared@services/rm-commands.wrapper';
import { versionCompare } from 'mbs-ui-shared@utils/version-compare';
import { Observable, defer, forkJoin, from, noop, of } from 'rxjs';
import { catchError, concatMap, filter, finalize, first, pairwise, switchMap, take } from 'rxjs/operators';
import { SidepanelDownloadsComponent } from '../../sidepanel-downloads/sidepanel-downloads.component';
import {
  UpdateRecommendedAnswer,
  UpdateRecommendedModalComponent
} from '../../update-recommended-modal/update-recommended-modal.component';
import { ComputersAsyncResultsModalComponent } from '../async-results-modal/computers-async-results-modal.component';
import { ForceUpdateAgentType } from './computers-force-update-modal-interface';
import { ForceUpdateAgentSection, forceUpdateAgentSections, forceUpdateOsKeys } from './computers-force-update-modal.constants';
import {
  ForceUpdateActionConfirmData,
  ForceUpdateActionConfirmModalComponent
} from './force-update-action-confirm-modal/force-update-action-confirm-modal.component';
import { ForceUpdateNewVersionAlertComponent } from './force-update-new-version-alert/force-update-new-version-alert.component';
import {
  BuildAction,
  BuildActionOffer,
  getGroupBuildOffers,
  getSingleBuildOffers
} from './force-update-new-version-utils/force-update-new-version';

type ForceUpdateOption = {
  value: ForceUpdateAgentType;
  versionType: BuildVersionType;
  version?: string;
};

type ForceUpdateAgentView = ForceUpdateAgentSection & {
  title: string;
  agent: AgentAvailableBuildVersions;
  options: ForceUpdateOption[];
  showOptions: boolean;
  canUpdate: boolean;
  statusIcon: string;
  buildOffers: BuildActionOffer[];
  busyActions: BuildAction[];
};

type PendingAction = {
  action: BuildAction;
  buildType: BuildType;
};

const optionValues: Record<BuildVersionType, ForceUpdateAgentType> = {
  [BuildVersionType.Public]: ForceUpdateAgentType.Public,
  [BuildVersionType.Sandbox]: ForceUpdateAgentType.Sandbox,
  [BuildVersionType.Beta]: ForceUpdateAgentType.Beta
};

const anyUpdateSelected: ValidatorFn = (form) =>
  Object.values(form.value).some(Boolean) ? null : { agent: { message: 'Not any build available version for updating' } };

@Component({
  selector: 'mbs-computers-force-update-modal',
  templateUrl: './computers-force-update-modal.component.html',
  styleUrls: ['./computers-force-update-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    ReactiveFormsModule,
    StatusComponent,
    ButtonComponent,
    AlertComponent,
    I18NextPipe,
    ModalComponent,
    RadioComponent,
    ModalFooterDirective,
    ForceUpdateNewVersionAlertComponent
  ]
})
export class ComputersForceUpdateModalComponent implements ComputerBasedModal, OnInit {
  private rmCommands = inject(RmCommandsWrapper);
  private facade = inject(ComputersFacade);
  private buildService = inject(BuildService);
  private buildsFacade = inject(BuildsFacade);
  private modalService = inject(ModalService);
  private sidepanelService = inject(SidepanelService);
  private toastService = inject(ToastService);
  private ability = inject(AbilityService);
  private destroyRef = inject(DestroyRef);
  private i18nPipe = inject(I18NextPipe);
  private currentUser = toSignal(inject(AuthFacade).currentUser$);

  public readonly alertType = MbsPopupType;
  public readonly labelSize = MbsLabelSize.sm;
  public readonly statusTextSize = MbsSize.sm;
  public readonly forceUpdateAgentType = ForceUpdateAgentType;
  public readonly versionLabelKeys: Record<BuildVersionType, string> = {
    [BuildVersionType.Public]: 'shared:download-build.versions.public',
    [BuildVersionType.Sandbox]: 'shared:download-build.versions.sandbox',
    [BuildVersionType.Beta]: 'shared:download-build.versions.beta'
  };
  public readonly elementSelectors = {
    name: {
      groupHint: 'force-update-group-hint',
      noAgentAlert: 'force-update-no-agent-alert',
      newVersionAlert: 'force-update-new-version-alert',
      section: 'force-update-section',
      status: 'force-update-status',
      option: 'force-update-option',
      updateButton: 'force-update-button'
    }
  };

  public computer: Computer = null;
  public mode = GroupActionMode.Single;
  public hids: string[] = [];
  public filters: Observable<GetComputersParams>;
  public legacyFilters = {
    searchQuery: null,
    unSupportedVersion: false,
    companyId: null,
    hidden: false
  };

  public readonly canManageBuilds = this.ability.can('read', PermissionsEnum.Rebranding);
  public readonly isLoading = signal(true);
  public readonly isSubmitting = signal(false);
  public readonly isSandBoxAvailable = signal(false);
  private readonly availableVersions = signal<ComputerAvailableVersions>(null);
  private readonly pendingActions = signal<PendingAction[]>([]);

  public readonly computerName = computed(() => this.availableVersions()?.displayName || this.availableVersions()?.name || '');
  public readonly installedAgents = computed(() =>
    forceUpdateAgentSections.map(({ agentType }) => this.getAgent(agentType)).filter(Boolean)
  );
  public readonly agentViews = computed(() => (this.isGroupAction ? this.getGroupAgentViews() : this.getSingleAgentViews()));
  public readonly hasUpdateOptions = computed(() => this.isGroupAction || this.agentViews().some(({ canUpdate }) => canUpdate));
  public readonly showReleaseNotes = computed(
    () => !!this.currentUser()?.IsProvider || this.ability.can('read', PermissionsEnum.HelpMarketing)
  );
  public readonly isAnyBuildInProgress = computed(() => !!this.pendingActions().length || !!this.buildsFacade.buildsInProgress()?.length);
  private readonly windowsPublicVersion = computed(
    () => this.buildsFacade.builds()?.find(({ mbsBuildType }) => mbsBuildType === BuildType.CommonWindowsBuild)?.public?.version
  );

  @ViewChild(ModalComponent, { static: true }) private baseModal: ModalComponent;

  public forceUpdateForm = new UntypedFormGroup(
    {
      backup: new FormControl(ForceUpdateAgentType.NotNecessary),
      rmm: new FormControl(ForceUpdateAgentType.NotNecessary),
      ra: new FormControl(ForceUpdateAgentType.NotNecessary)
    },
    [anyUpdateSelected]
  );

  public get isGroupAction(): boolean {
    return [GroupActionMode.Group, GroupActionMode.Bulk].includes(this.mode);
  }

  private get isWindowsComputer(): boolean {
    return Computer.isWindows(this.computer);
  }

  constructor() {
    toObservable(this.buildsFacade.buildsInProgress)
      .pipe(
        pairwise(),
        filter(([previous, current]) => !this.isGroupAction && !!previous?.some((buildType) => !current?.includes(buildType))),
        takeUntilDestroyed()
      )
      .subscribe(() => this.reloadAvailableVersions());
  }

  ngOnInit(): void {
    if (this.isGroupAction) {
      this.buildService
        .isSandBoxAvailable()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((isSandBoxAvailable) => {
          this.isSandBoxAvailable.set(isSandBoxAvailable);
          this.updateForm();
          this.isLoading.set(false);
        });

      return;
    }

    forkJoin([this.buildService.isSandBoxAvailable(), this.facade.getAvailableVersions(this.computer.hid)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([isSandBoxAvailable, availableVersions]) => {
        this.isSandBoxAvailable.set(isSandBoxAvailable);
        this.availableVersions.set(availableVersions);
        this.updateForm();
        this.isLoading.set(false);
      });
  }

  handleResolve(): void {
    if (!this.isUpdateToRevokedCertificateBuild()) return void this.resolveForceUpdate();

    const question = this.i18nPipe.transform('shared:download-build.updateRecommended.generateQuestion');

    from(this.modalService.openCustom<UpdateRecommendedAnswer>(UpdateRecommendedModalComponent, { data: { question } }))
      .pipe(
        // The cross rejects, so closing the modal leaves the force update untouched
        catchError(() => of(null)),
        take(1)
      )
      .subscribe((answer) => {
        if (answer === UpdateRecommendedAnswer.Yes) return void this.generateNewBuild();

        if (answer === UpdateRecommendedAnswer.No) this.resolveForceUpdate();
      });
  }

  runBuildAction(offer: BuildActionOffer, productKey: string): void {
    const data: ForceUpdateActionConfirmData = {
      offer,
      productKey,
      showReleaseNotes: this.showReleaseNotes(),
      isAutoUpdateEnabled: !!this.buildsFacade.settings()?.isAutoUpdateEnabled
    };

    from(this.modalService.openCustom<boolean>(ForceUpdateActionConfirmModalComponent, { data }))
      .pipe(
        catchError(() => of(false)),
        take(1)
      )
      .subscribe((confirmed) => {
        if (!confirmed) return;

        if (offer.action === BuildAction.MakePublic) return void this.makePublic(offer);

        this.requestNewVersion(offer);
      });
  }

  private requestNewVersion(offer: BuildActionOffer): void {
    this.startPending(offer);

    this.buildService
      .requestBuild({ buildsTypeList: offer.buildTypes, isBuildDestinationSandbox: offer.action === BuildAction.GenerateSandbox })
      .pipe(finalize(() => this.finishPending(offer)))
      .subscribe({
        next: (res) => this.showResponse(res),
        error: noop
      });
  }

  private makePublic(offer: BuildActionOffer): void {
    const builds = offer.buildTypes
      .map((buildType) => this.buildsFacade.builds()?.find(({ mbsBuildType }) => mbsBuildType === buildType))
      .filter(Boolean);

    this.startPending(offer);

    from(builds)
      .pipe(
        concatMap((build) => this.buildsFacade.makePublic(build)),
        finalize(() => this.finishPending(offer))
      )
      .subscribe({
        next: (res) => this.showResponse(res),
        error: noop,
        complete: () => {
          if (!this.isGroupAction && !this.destroyRef.destroyed) this.reloadAvailableVersions();
        }
      });
  }

  private startPending({ action, buildTypes }: BuildActionOffer): void {
    this.pendingActions.update((pending) => [...pending, ...buildTypes.map((buildType) => ({ action, buildType }))]);
  }

  private finishPending({ action, buildTypes }: BuildActionOffer): void {
    this.pendingActions.update((pending) => pending.filter((item) => item.action !== action || !buildTypes.includes(item.buildType)));
  }

  private showResponse(res: CommonResponseType): void {
    this.toastService[res?.success ? 'success' : 'error'](res?.message);
  }

  private reloadAvailableVersions(): void {
    this.facade
      .getAvailableVersions(this.computer.hid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((availableVersions) => {
        this.availableVersions.set(availableVersions);
        this.updateForm();
      });
  }

  private getDownloadBuild(agentType: AgentType): Build {
    const buildType = getDownloadBuildType(this.computer, agentType);

    return this.buildsFacade.builds()?.find(({ mbsBuildType }) => mbsBuildType === buildType) ?? null;
  }

  private getAgent(agentType: AgentType): AgentAvailableBuildVersions {
    return this.availableVersions()?.applications?.find((app) => app.applicationId.toLowerCase() === agentType) ?? null;
  }

  private getPermittedSections(): ForceUpdateAgentSection[] {
    return forceUpdateAgentSections.filter(({ permission }) => this.ability.can('read', permission));
  }

  private getSingleAgentViews(): ForceUpdateAgentView[] {
    return this.getPermittedSections()
      .map((section) => ({ section, agent: this.getAgent(section.agentType) }))
      .filter(({ agent }) => !!agent)
      .map(({ section, agent }) => {
        const options = section.versionTypes
          .filter((versionType) => this.isNewerVersionAvailable(agent, versionType))
          .map((versionType) => ({ value: optionValues[versionType], versionType, version: agent.builds[versionType].version }));
        const isUpdatable = agent.online && agent.forceUpdateSupporting;
        const buildOffers = getSingleBuildOffers(agent, this.getDownloadBuild(section.agentType), this.isSandBoxAvailable());

        return {
          ...section,
          title: this.getSectionTitle(section),
          agent,
          options,
          showOptions: isUpdatable && (!!options.length || !!buildOffers.length),
          canUpdate: isUpdatable && !!options.length,
          statusIcon: this.getStatusIcon(agent),
          ...this.getBuildOffersState(buildOffers)
        };
      });
  }

  private isOfferBusy({ action, buildTypes }: BuildActionOffer): boolean {
    const isGenerating = action !== BuildAction.MakePublic;

    return buildTypes.some(
      (buildType) =>
        this.pendingActions().some((pending) => pending.action === action && pending.buildType === buildType) ||
        (isGenerating && !!this.buildsFacade.buildsInProgress()?.includes(buildType))
    );
  }

  private getGroupAgentViews(): ForceUpdateAgentView[] {
    return this.getPermittedSections().map((section) => ({
      ...section,
      title: this.getSectionTitle(section),
      agent: null,
      options: section.versionTypes
        .filter(
          (versionType) =>
            versionType === BuildVersionType.Public || (versionType === BuildVersionType.Sandbox && this.isSandBoxAvailable())
        )
        .map((versionType) => ({ value: optionValues[versionType], versionType })),
      showOptions: true,
      canUpdate: true,
      statusIcon: null,
      ...this.getBuildOffersState(getGroupBuildOffers(section.agentType, this.buildsFacade.builds(), this.isSandBoxAvailable()))
    }));
  }

  private getBuildOffersState(buildOffers: BuildActionOffer[]): Pick<ForceUpdateAgentView, 'buildOffers' | 'busyActions'> {
    return {
      buildOffers,
      busyActions: buildOffers.filter((offer) => this.isOfferBusy(offer)).map(({ action }) => action)
    };
  }

  private getSectionTitle({ productKey }: ForceUpdateAgentSection): string {
    const product = this.i18nPipe.transform(productKey);
    const osKey = forceUpdateOsKeys[this.computer?.os];

    if (this.isGroupAction || !osKey) return this.i18nPipe.transform('shared:forceUpdate.agentTitle', { product });

    return this.i18nPipe.transform('shared:forceUpdate.agentTitleForOs', { product, os: this.i18nPipe.transform(osKey) });
  }

  private isNewerVersionAvailable(agent: AgentAvailableBuildVersions, versionType: BuildVersionType): boolean {
    if (versionType === BuildVersionType.Sandbox && !this.isSandBoxAvailable()) return false;

    const version = agent.builds?.[versionType]?.version;

    return !!version && versionCompare(version, agent.currentVersion) > 0;
  }

  private getStatusIcon(agent: AgentAvailableBuildVersions): string {
    if (!agent.online) return 'ico ico-pause-circle text-muted';

    return agent.forceUpdateSupporting ? 'ico ico-success text-success' : 'ico ico-exclamation-circle text-danger';
  }

  private updateForm(): void {
    const views = this.agentViews();

    this.forceUpdateForm.patchValue(
      Object.fromEntries(
        forceUpdateAgentSections.map(({ agentType }) => {
          const selected = this.forceUpdateForm.get(agentType).value;
          const isSelectedAvailable = views.find((view) => view.agentType === agentType)?.options.some(({ value }) => value === selected);

          if (isSelectedAvailable) return [agentType, selected];

          return [agentType, this.isGroupAction || this.getAgent(agentType) ? ForceUpdateAgentType.NotNecessary : null];
        })
      )
    );
  }

  private isUpdateToRevokedCertificateBuild(): boolean {
    if (this.isSandBoxAvailable()) return false;

    const updatesToPublic = Number(this.forceUpdateForm.get(AgentType.Backup)?.value) === ForceUpdateAgentType.Public;

    return updatesToPublic && isRevokedCertificateVersion(this.targetPublicVersion);
  }

  /*
   * A group selection is never known to be Windows-only, so there the warning is about the build itself:
   * if the public Windows build is revoked, every Windows machine in the selection would get it
   */
  private get targetPublicVersion(): string {
    if (this.isGroupAction) return this.windowsPublicVersion();

    return this.isWindowsComputer ? this.getAgent(AgentType.Backup)?.builds?.public?.version : null;
  }

  private generateNewBuild(): void {
    this.baseModal.close();

    this.buildService
      .requestBuild({
        buildsTypeList: [this.isGroupAction ? BuildType.CommonWindowsBuild : getDownloadBuildType(this.computer, AgentType.Backup)],
        isBuildDestinationSandbox: false
      })
      .pipe(take(1))
      .subscribe((res) => this.showResponse(res));

    this.openDownloadsPanel();
  }

  private openDownloadsPanel(): void {
    this.sidepanelService
      .closeAll()
      .pipe(
        first(),
        switchMap(() => {
          this.sidepanelService.get(SidepanelDownloadsComponent) || this.sidepanelService.add(SidepanelDownloadsComponent);

          return this.sidepanelService.openByType(SidepanelDownloadsComponent);
        }),
        take(1)
      )
      .subscribe({ next: noop, error: noop });
  }

  private resolveForceUpdate(): void {
    const forceUpdateText = this.i18nPipe.transform('shared:forceUpdate.title');

    this.isSubmitting.set(true);

    this.facade
      .handleGroupOperation(defer(() => this.sendForceUpdate()).pipe(catchError(() => of(false))), {
        operationText: forceUpdateText,
        modalOperationTitle: `${forceUpdateText} ${this.i18nPipe.transform('shared:common.details')}`,
        isGroupAction: this.isGroupAction,
        detailModal: ComputersAsyncResultsModalComponent
      })
      .pipe(take(1))
      .subscribe(() => {
        this.baseModal.close();
        this.isSubmitting.set(false);
        this.facade.invalidate();
      });
  }

  private sendForceUpdate(): Observable<unknown> {
    const updateBuilds = this.prepareUpdateBuilds();
    const appIds = updateBuilds.map(({ appId }) => appId as AgentType);

    switch (this.mode) {
      case GroupActionMode.Legacy:
        return this.rmCommands.bulkForceUpdate(
          {
            search: this.legacyFilters.searchQuery,
            companyIds: [this.legacyFilters.companyId],
            loadHidden: this.legacyFilters.hidden,
            type: this.legacyFilters.unSupportedVersion ? ComputersFiltersType.Unsupported : ComputersFiltersType.None,
            appIds
          },
          updateBuilds,
          appIds
        );
      case GroupActionMode.Bulk:
        return this.filters.pipe(
          take(1),
          switchMap((filters) => this.rmCommands.bulkForceUpdate({ ...filters, appIds }, updateBuilds, appIds))
        );
      default:
        return this.rmCommands.groupForceUpdate(
          this.prepareAgentCommands(this.mode === GroupActionMode.Group ? this.hids : [this.computer.hid], updateBuilds)
        );
    }
  }

  private prepareAgentCommands(hids: string[], updateBuilds: UpdateBuild[]): AgentCommand<ParamsForceUpdate>[] {
    return hids.flatMap((computerHid) =>
      updateBuilds.map(({ appId }) => ({ computerHid, agentType: appId as AgentType, params: { updateBuilds } }))
    );
  }

  private prepareUpdateBuilds(): UpdateBuild[] {
    return Object.entries(this.forceUpdateForm.value)
      .filter(([, value]) => Number(value) > 0)
      .map(([appId, value]) => ({ appId, versionType: ForceUpdateAgentType[value as ForceUpdateAgentType] }));
  }
}
